import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import z from "zod";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiButton,
  MiControlledTextField,
  MiPlaceholderArea,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  OPTION_STATUS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { IBidOptionResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCheck, mdiClose, mdiPencilOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { OptionPriceDetailsDrawer } from "./OptionPriceDetailsDrawer";

const schema = z.object({
  name: z.string().min(1, "Option name is required"),
});

export function OptionDetails({
  option,
  handleChangeOptionName,
  handleUpdateOption,
  handleCancelOptionEdit,
}: OptionDetailsProps) {
  const [isEditingName, setIsEditingName] = useState(false);

  const { classes, theme } = useStyles();
  const {
    formState: { isValid },
    control,
    reset,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues: { name: option.name },
    resolver: zodResolver(schema),
  });

  const isOptionModified = useMemo(() => {
    return Boolean(
      option?.planAreas?.some(
        (area) =>
          area.optionStatus === OPTION_STATUS.ADDED ||
          area.optionStatus === OPTION_STATUS.REMOVED ||
          area.materials?.some(
            (m) =>
              m.optionStatus === OPTION_STATUS.ADDED ||
              m.optionStatus === OPTION_STATUS.REMOVED
          )
      )
    );
  }, [option]);

  useEffect(() => {
    reset({ name: option.name });
  }, [option.name]);

  const handleConfirmName = () => {
    const { name } = getValues();
    handleChangeOptionName(name);
    setIsEditingName(false);
  };

  const handleCancelName = () => {
    reset({ name: option.name });
    setIsEditingName(false);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        {isEditingName ? (
          <Box className={classes.nameEditBox}>
            <MiControlledTextField
              name="name"
              label="Option Name"
              formControl={control}
              className={classes.nameInput}
            />
            <MiButton
              startIcon={{ path: mdiClose, iconSize: 0.7 }}
              color={BUTTON_COLORS.RED}
              onClick={handleCancelName}
            />
            <MiButton
              startIcon={{ path: mdiCheck, iconSize: 0.7 }}
              color={BUTTON_COLORS.SUCCESS}
              disabled={!isValid}
              onClick={handleConfirmName}
            />
          </Box>
        ) : (
          <Box className={classes.nameBox}>
            <Typography
              className={classes.optionName}
              variant={TYPOGRAPHY_VARIANTS.BODY1}
            >
              {`Option Name: `}
              <span className={classes.highlight}>{option.name}</span>
            </Typography>
            <MiButton
              startIcon={{ path: mdiPencilOutline, iconSize: 0.7 }}
              color={BUTTON_COLORS.GREY}
              onClick={() => setIsEditingName(true)}
            />
          </Box>
        )}
        <Box className={classes.actions}>
          <MiButton
            title="Cancel"
            variant={BUTTON_VARIANTS.TEXT}
            color={BUTTON_COLORS.BLACK}
            sx={{ textDecoration: "underline" }}
            onClick={handleCancelOptionEdit}
          />
          <MiButton
            title="Save Option"
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={handleUpdateOption}
            disabled={!isValid}
          />
        </Box>
      </Box>
      {isOptionModified ? (
        <OptionPriceDetailsDrawer option={option} />
      ) : (
        <MiPlaceholderArea
          showLogo
          helperText={
            "Add and/or remove items or areas on \n the bid to begin creating your option."
          }
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            whiteSpace: "pre-wrap",
            height: "100%",
            marginTop: theme.spacing(2),
          }}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  nameBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  nameEditBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  nameInput: {
    backgroundColor: theme.palette.common.white,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
  },
  optionName: {
    fontWeight: theme.fontWeight.bold,
  },
  highlight: {
    color: theme.palette.primary.main,
  },
}));

interface OptionDetailsProps {
  option: IBidOptionResponse;
  handleChangeOptionName: (name: string) => void;
  handleUpdateOption: () => void;
  handleCancelOptionEdit: () => void;
}
